import React, { createContext, useContext, useState, useEffect } from 'react';
import { getCmsData, saveCmsData, resetCmsData, fetchCmsFromBackend, installImgErrorHandler } from './cmsData';

const CmsContext = createContext();

// Inject CMS style overrides into a <style> tag so they apply site-wide
const injectCmsStyleOverrides = () => {
  try {
    const raw = localStorage.getItem('cmsStyleOverrides');
    if (!raw) return;
    const overrides = JSON.parse(raw);
    if (!overrides || typeof overrides !== 'object') return;
    let el = document.getElementById('cms-live-style-overrides');
    if (!el) {
      el = document.createElement('style');
      el.id = 'cms-live-style-overrides';
      document.head.appendChild(el);
    }
    let css = '';
    for (const [selector, props] of Object.entries(overrides)) {
      if (selector && props && props.trim()) {
        // Add !important to every property so CMS overrides beat site CSS
        const boosted = props.split(';').map(rule => {
          const trimmed = rule.trim();
          if (!trimmed) return '';
          if (trimmed.toLowerCase().includes('!important')) return trimmed;
          return trimmed + ' !important';
        }).filter(Boolean).join('; ');
        css += `${selector} { ${boosted}; }\n`;
      }
    }
    el.textContent = css;
  } catch { /* ignore parse errors */ }
};

export const useCms = () => {
  const context = useContext(CmsContext);
  if (!context) {
    throw new Error('useCms must be used within a CmsProvider');
  }
  return context;
};

export const CmsProvider = ({ children }) => {
  const [cmsContent, setCmsContent] = useState(getCmsData());
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Install global image error handler (catches broken /media/ URLs, swaps to fallback)
    installImgErrorHandler();

    // Inject style overrides on every page load
    injectCmsStyleOverrides();

    const handleCmsUpdate = (event) => {
      const latestData = getCmsData();
      setCmsContent(latestData);
      injectCmsStyleOverrides();
    };

    window.addEventListener('cmsDataUpdated', handleCmsUpdate);
    
    const handleStorageChange = (e) => {
      if (e.key === 'cmsData') {
        const latestData = getCmsData();
        setCmsContent(latestData);
      }
      if (e.key === 'cmsStyleOverrides') {
        injectCmsStyleOverrides();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);

    // When a CMS image 404s, re-render so getCmsImagePath returns fallback
    const handleBrokenImage = () => setCmsContent(prev => ({ ...prev }));
    window.addEventListener('cmsImageBroken', handleBrokenImage);

    // Fetch latest CMS data from backend (source of truth for all devices)
    fetchCmsFromBackend().then((backendData) => {
      if (backendData) {
        setCmsContent(backendData);
        // Re-inject style overrides from backend data
        injectCmsStyleOverrides();
      }
    });
    
    return () => {
      window.removeEventListener('cmsDataUpdated', handleCmsUpdate);
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('cmsImageBroken', handleBrokenImage);
    };
  }, []);

  const updateContent = async (newContent) => {
    setIsLoading(true);
    try {
      await saveCmsData(newContent);
      setCmsContent(newContent);
      return true;
    } catch (error) {
      console.error('Error saving CMS content:', error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const resetToDefaults = async () => {
    const defaultData = await resetCmsData();
    setCmsContent(defaultData);
    return defaultData;
  };

  const value = {
    content: cmsContent,
    updateContent,
    resetToDefaults,
    isLoading,
  };

  return (
    <CmsContext.Provider value={value}>
      {children}
    </CmsContext.Provider>
  );
};

export default CmsContext;
